import React, { useState } from 'react';
import { useCreateQuizMutation, useAddQuestionToQuizMutation, useUpdateCourseFeaturesMutation } from '../redux/apiSlice';

const SectionQuizComponent = ({ sectionId, sectionName, showToast, courseId, isLocked: initialIsLocked, refetchCourse, existingQuizzes = [] }) => {
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [quizzes, setQuizzes] = useState(existingQuizzes); // Store multiple quizzes from props
  const [currentQuizIndex, setCurrentQuizIndex] = useState(null);
  
  // New quiz form state
  const [quizTitle, setQuizTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentOptions, setCurrentOptions] = useState([
    { text: '', isCorrect: true },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false }
  ]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizStatus, setQuizStatus] = useState(initialIsLocked ? 'locked' : 'unlocked');
  const [updatingQuizId, setUpdatingQuizId] = useState(null); // Track which quiz is being updated

  const [createQuiz] = useCreateQuizMutation();
  const [addQuestionToQuiz] = useAddQuestionToQuizMutation();
  const [updateCourseFeatures] = useUpdateCourseFeaturesMutation();

  // Debug: Log quizzes when component mounts or updates
  React.useEffect(() => {
    console.log('📋 SectionQuizComponent - Received existingQuizzes:', existingQuizzes);
    console.log('📋 SectionQuizComponent - Quizzes state:', quizzes);
  }, [existingQuizzes, quizzes]);

  // Reset form for new quiz
  const resetQuizForm = () => {
    setQuizTitle('');
    setQuestions([]);
    setCurrentQuestion('');
    setCurrentOptions([
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]);
    setQuizStatus(initialIsLocked ? 'locked' : 'unlocked');
  };

  // Start creating new quiz
  const handleStartNewQuiz = () => {
    resetQuizForm();
    setCurrentQuizIndex(null);
    setShowQuizForm(true);
  };

  // Edit existing quiz
  const handleEditQuiz = (quizIndex) => {
    const quiz = quizzes[quizIndex];
    setQuizTitle(quiz.title);
    setQuestions(quiz.questions || []);
    setQuizStatus(quiz.status || (quiz.isLocked ? 'locked' : 'unlocked'));
    setCurrentQuizIndex(quizIndex);
    setShowQuizForm(true);
  };

  // Delete quiz
  const handleDeleteQuiz = (quizIndex) => {
    if (window.confirm('Are you sure you want to delete this quiz?')) {
      const updatedQuizzes = quizzes.filter((_, index) => index !== quizIndex);
      setQuizzes(updatedQuizzes);
      showToast('Quiz deleted successfully', 'success');
    }
  };

  const handleAddQuestion = () => {
    if (!currentQuestion.trim()) {
      showToast('Please enter a question', 'error');
      return;
    }

    const validOptions = currentOptions.filter(option => option.text.trim());
    if (validOptions.length < 2) {
      showToast('Please add at least 2 options', 'error');
      return;
    }

    const correctOptions = validOptions.filter(option => option.isCorrect);
    if (correctOptions.length !== 1) {
      showToast('Please mark exactly one option as correct', 'error');
      return;
    }

    const newQuestion = {
      id: Date.now(),
      question: currentQuestion,
      options: [...validOptions]
    };

    setQuestions([...questions, newQuestion]);
    setCurrentQuestion('');
    setCurrentOptions([
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]);

    showToast('Question added successfully', 'success');
  };

  const handleRemoveQuestion = (index) => {
    const updatedQuestions = [...questions];
    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);
    showToast('Question removed', 'info');
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...currentOptions];
    
    if (field === 'isCorrect') {
      updatedOptions.forEach((option, i) => {
        option.isCorrect = i === index;
      });
    } else {
      updatedOptions[index][field] = value;
    }
    
    setCurrentOptions(updatedOptions);
  };

  const handleLockToggle = async (quizIndex, value) => {
    const quiz = quizzes[quizIndex];
    const newLockStatus = value === 'locked';
    
    // If quiz doesn't have an ID (not saved to server yet), just update local state
    if (!quiz.id) {
      const updatedQuizzes = [...quizzes];
      updatedQuizzes[quizIndex] = { ...quiz, status: value, isLocked: newLockStatus };
      setQuizzes(updatedQuizzes);
      showToast(`Quiz ${newLockStatus ? 'locked' : 'unlocked'} (Local Only)`, 'info');
      return;
    }
    
    // Set only this quiz as updating
    setUpdatingQuizId(quiz.id);
    
    // Update local state immediately for UI responsiveness
    const updatedQuizzes = [...quizzes];
    updatedQuizzes[quizIndex] = {
      ...quiz,
      isLocked: newLockStatus,
      status: value,
      lockUpdateStatus: 'updating'
    };
    setQuizzes(updatedQuizzes);
    
    try {
      console.log('🔒 Updating quiz lock status via API:', {
        quizId: quiz.id,
        islock: newLockStatus,
        courseId: courseId
      });
      
      const result = await updateCourseFeatures({
        courseId: parseInt(courseId, 10),
        videoIds: [],
        pdfIds: [],
        quizIds: [
          {
            id: parseInt(quiz.id, 10),
            islock: newLockStatus
          }
        ]
      }).unwrap();
      
      console.log('✅ Quiz lock status updated successfully:', result);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // ✅ FIXED: Only try to refetch if refetchCourse exists and we're in edit mode
      if (refetchCourse && typeof refetchCourse === 'function') {
        try {
          await refetchCourse();
          console.log('✅ Course data refetched successfully');
        } catch (refetchError) {
          // This error is expected in create mode, so we can ignore it
          console.log('⚠️ Could not refetch course (expected in create mode):', refetchError.message);
          // Don't show error to user, just log it
        }
      } else {
        console.log('ℹ️ Skipping refetch - no refetchCourse function provided or not in edit mode');
      }
      
      const finalQuizzes = [...quizzes];
      finalQuizzes[quizIndex] = {
        ...quiz,
        isLocked: newLockStatus,
        status: value,
        lockUpdateStatus: null
      };
      setQuizzes(finalQuizzes);
      
      showToast(`Quiz ${newLockStatus ? 'locked' : 'unlocked'} successfully`, 'success');
      
    } catch (error) {
      console.error('❌ Failed to update quiz lock status:', error);
      
      // Revert on error
      const revertQuizzes = [...quizzes];
      revertQuizzes[quizIndex] = {
        ...quiz,
        isLocked: !newLockStatus,
        status: !newLockStatus ? 'locked' : 'unlocked',
        lockUpdateStatus: null
      };
      setQuizzes(revertQuizzes);
      
      let errorMessage = 'Failed to update quiz lock status. ';
      if (error.data?.message) {
        errorMessage += error.data.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setUpdatingQuizId(null);
    }
  };

  const handleCreateQuiz = async () => {
    if (!quizTitle.trim()) {
      showToast('Please enter a quiz title', 'error');
      return;
    }

    if (questions.length === 0) {
      showToast('Please add at least one question', 'error');
      return;
    }

    if (!sectionId) {
      showToast('Section ID is required', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // If editing existing quiz that was already saved
      if (currentQuizIndex !== null && quizzes[currentQuizIndex]?.id) {
        // Update local state only (for now)
        const updatedQuizzes = [...quizzes];
        updatedQuizzes[currentQuizIndex] = {
          ...updatedQuizzes[currentQuizIndex],
          title: quizTitle,
          questions: questions,
          totalQuestions: questions.length,
          status: quizStatus,
          isLocked: quizStatus === 'locked'
        };
        setQuizzes(updatedQuizzes);
        showToast('🎉 Quiz updated successfully!', 'success');
        setShowQuizForm(false);
        setIsSubmitting(false);
        return;
      }

      // Create new quiz
      const quizResult = await createQuiz({
        title: quizTitle,
        sectionId: sectionId
      }).unwrap();

      console.log('✅ Section Quiz created:', quizResult);
      
      // Add all questions to the quiz
      for (const question of questions) {
        await addQuestionToQuiz({
          quizId: quizResult.quiz.id,
          question: question.question,
          options: question.options
        }).unwrap();
      }

      // If quiz should be locked, update lock status
      if (quizStatus === 'locked') {
        await updateCourseFeatures({
          courseId: parseInt(courseId, 10),
          videoIds: [],
          pdfIds: [],
          quizIds: [
            {
              id: parseInt(quizResult.quiz.id, 10),
              islock: true
            }
          ]
        }).unwrap();
      }

      // ✅ FIXED: Only try to refetch if refetchCourse exists and we're in edit mode
      if (refetchCourse && typeof refetchCourse === 'function') {
        try {
          await refetchCourse();
          console.log('✅ Course data refetched successfully after quiz creation');
        } catch (refetchError) {
          // This error is expected in create mode, so we can ignore it
          console.log('⚠️ Could not refetch course after quiz creation (expected in create mode):', refetchError.message);
          // Don't show error to user, just log it
        }
      } else {
        console.log('ℹ️ Skipping refetch - no refetchCourse function provided or not in edit mode');
      }

      // Add new quiz to quizzes list with ALL questions
      const newQuiz = {
        id: quizResult.quiz.id,
        title: quizResult.quiz.title,
        sectionId: quizResult.quiz.sectionId,
        createdAt: quizResult.quiz.createdAt,
        questions: questions, // Store all questions
        totalQuestions: questions.length,
        isLocked: quizStatus === 'locked',
        status: quizStatus
      };

      setQuizzes([...quizzes, newQuiz]);
      showToast('🎉 Section Quiz created successfully!', 'success');
      setShowQuizForm(false);
      
    } catch (error) {
      console.error('❌ Failed to create section quiz:', error);
      
      let errorMessage = 'Failed to create section quiz. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 400) {
        errorMessage = 'Bad request. Please check section ID.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to render questions preview with scroll bar
  const renderQuestionsPreview = (questions) => {
    if (!questions || questions.length === 0) return null;
    
    return (
      <div className="mt-3 border-t border-gray-100 pt-3">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs font-medium text-gray-500">
            Questions Preview ({questions.length} total)
          </p>
          <span className="text-xs text-gray-400">
            Scroll to see all questions ↓
          </span>
        </div>
        <div className="max-h-60 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
          {questions.map((q, idx) => (
            <div key={idx} className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
              <div className="flex items-start">
                <span className="text-gray-500 font-medium mr-2 min-w-[20px]">{idx + 1}.</span>
                <div className="flex-1">
                  <span className="text-gray-800 font-medium block mb-2">{q.question || q.text}</span>
                  <div className="ml-2 space-y-1.5">
                    {(q.options || []).map((opt, optIdx) => (
                      <div key={optIdx} className="flex items-center text-xs">
                        <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${opt.isCorrect ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        <span className={opt.isCorrect ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {opt.text} {opt.isCorrect && '✓'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Scroll bar height indicator */}
        {questions.length > 3 && (
          <div className="mt-2 text-xs text-gray-400 text-right">
            Showing all {questions.length} questions
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={handleStartNewQuiz}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium text-sm hover:bg-blue-600 transition-colors duration-300 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span>{showQuizForm ? 'Hide Quiz Form' : 'Add New Quiz'}</span>
          </button>
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
            {quizzes.length} Quiz{quizzes.length !== 1 ? 'zes' : ''}
          </span>
        </div>
        
        <div className="text-sm text-gray-600">
          Section: {sectionName}
        </div>
      </div>

      {/* Display all quizzes */}
      {quizzes.length > 0 && !showQuizForm && (
        <div className="space-y-4 mb-4">
          {quizzes.map((quiz, quizIndex) => (
            <div key={quizIndex} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 flex items-center">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded mr-2">
                      Quiz #{quizIndex + 1}
                    </span>
                    {quiz.title}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {quiz.totalQuestions || quiz.questions?.length || 0} Questions • Created: {formatDate(quiz.createdAt)}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Lock/Unlock Controls - Show loader only for this quiz */}
                  <div className="flex items-center space-x-1 bg-gray-50 p-1 rounded-lg border border-gray-200 mr-2">
                    <button
                      onClick={() => handleLockToggle(quizIndex, 'unlocked')}
                      disabled={updatingQuizId === quiz.id}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        quiz.status === 'unlocked' || (!quiz.status && !quiz.isLocked)
                          ? 'bg-green-100 text-green-700 border border-green-300' 
                          : 'text-gray-500 hover:bg-gray-100'
                      } ${updatingQuizId === quiz.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Unlock
                    </button>
                    <button
                      onClick={() => handleLockToggle(quizIndex, 'locked')}
                      disabled={updatingQuizId === quiz.id}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        quiz.status === 'locked' || quiz.isLocked
                          ? 'bg-red-100 text-red-700 border border-red-300' 
                          : 'text-gray-500 hover:bg-gray-100'
                      } ${updatingQuizId === quiz.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Lock
                    </button>
                    {updatingQuizId === quiz.id && (
                      <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin ml-1"></div>
                    )}
                  </div>

                  {/* Edit button */}
                  <button
                    onClick={() => handleEditQuiz(quizIndex)}
                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                    title="Edit Quiz"
                    disabled={updatingQuizId === quiz.id}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={() => handleDeleteQuiz(quizIndex)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    title="Delete Quiz"
                    disabled={updatingQuizId === quiz.id}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Questions Preview with Scroll Bar */}
              {renderQuestionsPreview(quiz.questions)}
            </div>
          ))}
        </div>
      )}

      {/* Quiz Creation/Edit Form */}
      {showQuizForm && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">
                {currentQuizIndex !== null ? 'Edit Quiz' : 'Create New Quiz'}
              </h4>
              <p className="text-xs text-blue-700 mb-2">
                {currentQuizIndex !== null ? 'Update your quiz questions' : 'Add a new quiz to this section'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-medium">
                Section ID: {sectionId}
              </div>
              
              <div className="flex items-center space-x-2 bg-white p-1 rounded-lg border border-blue-200">
                <label className="flex items-center space-x-1 cursor-pointer px-2 py-1 rounded hover:bg-blue-50">
                  <input
                    type="radio"
                    name={`quiz-form-lock-${sectionId}`}
                    value="unlocked"
                    checked={quizStatus === 'unlocked'}
                    onChange={() => setQuizStatus('unlocked')}
                    className="w-3 h-3 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-xs text-gray-700">Unlock</span>
                </label>
                <label className="flex items-center space-x-1 cursor-pointer px-2 py-1 rounded hover:bg-blue-50">
                  <input
                    type="radio"
                    name={`quiz-form-lock-${sectionId}`}
                    value="locked"
                    checked={quizStatus === 'locked'}
                    onChange={() => setQuizStatus('locked')}
                    className="w-3 h-3 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-xs text-gray-700">Lock</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-xs font-medium text-blue-700 mb-1">
              Quiz Title *
            </label>
            <input
              type="text"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
              placeholder="Enter quiz title (e.g., 'Section 1 Knowledge Check')"
              className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-blue-700 mb-1">
              Question *
            </label>
            <input
              type="text"
              value={currentQuestion}
              onChange={(e) => setCurrentQuestion(e.target.value)}
              placeholder="Enter your question here"
              className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              required
            />

            <div className="space-y-2">
              <label className="block text-xs font-medium text-blue-700 mb-1">
                Options (Mark one as correct) *
              </label>
              {currentOptions.map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={option.isCorrect}
                    onChange={() => handleOptionChange(index, 'isCorrect', true)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {!option.text.trim() && (
                    <span className="text-xs text-gray-400">Required</span>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddQuestion}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors duration-300 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add This Question</span>
            </button>
          </div>

          {questions.length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs font-medium text-blue-700 mb-2">
                Added Questions ({questions.length})
              </h5>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {questions.map((q, index) => (
                  <div key={q.id} className="bg-white border border-blue-100 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{q.question}</p>
                        <div className="mt-1 space-y-1">
                          {q.options.map((opt, optIndex) => (
                            <div key={optIndex} className="flex items-center space-x-2">
                              <div className={`w-2 h-2 rounded-full ${opt.isCorrect ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                              <span className={`text-xs ${opt.isCorrect ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                                {opt.text}
                                {opt.isCorrect && ' (Correct)'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveQuestion(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove question"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t border-blue-200">
            <button
              type="button"
              onClick={() => {
                setShowQuizForm(false);
                setCurrentQuizIndex(null);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300 text-sm flex items-center space-x-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Cancel</span>
            </button>
            
            <button
              type="button"
              onClick={handleCreateQuiz}
              disabled={isSubmitting || questions.length === 0}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{currentQuizIndex !== null ? 'Update Quiz' : 'Create Quiz'} ({questions.length} questions)</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {!showQuizForm && quizzes.length === 0 && (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-xs text-gray-600 mb-1">
                <span className="font-medium">Section Quizzes:</span> Create multiple quizzes for this section. Each quiz will be displayed separately.
              </p>
              <p className="text-xs text-gray-500">
                Click "Add New Quiz" to create your first quiz. You can add as many quizzes as you need.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default SectionQuizComponent;